package nl.appstuff.padelchecker.site.localization

import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.runtime.staticCompositionLocalOf
import kotlinx.browser.localStorage
import kotlinx.browser.window
import org.w3c.dom.get
import org.w3c.dom.set

enum class SiteLanguage(val tag: String) {
    NL("nl"),
    EN("en"),
    ;

    companion object {
        fun fromTag(tag: String?): SiteLanguage = entries.firstOrNull { it.tag == tag } ?: NL
    }
}

private const val STORAGE_KEY = "padelchecker.site.language"

val LocalSiteLanguage = staticCompositionLocalOf { SiteLanguage.NL }
val LocalSiteStrings = staticCompositionLocalOf<SiteStrings> { NlSiteStrings }

/**
 * Reads the persisted language from localStorage (falling back to the browser's language)
 * and provides both [LocalSiteLanguage] and the matching [LocalSiteStrings] to descendants.
 *
 * Returns a setter that persists the choice to localStorage and updates the composition.
 */
class SiteLanguageController(
    val language: SiteLanguage,
    private val onSet: (SiteLanguage) -> Unit,
) {
    fun set(newLanguage: SiteLanguage) {
        onSet(newLanguage)
    }

    fun toggle() {
        val next = if (language == SiteLanguage.NL) SiteLanguage.EN else SiteLanguage.NL
        set(next)
    }
}

val LocalSiteLanguageController = staticCompositionLocalOf<SiteLanguageController> {
    SiteLanguageController(SiteLanguage.NL) {}
}

@Composable
fun SiteLocaleProvider(content: @Composable () -> Unit) {
    var language by remember {
        mutableStateOf(readInitialLanguage())
    }
    val strings = remember(language) {
        when (language) {
            SiteLanguage.NL -> NlSiteStrings
            SiteLanguage.EN -> EnSiteStrings
        }
    }
    val controller = remember(language) {
        SiteLanguageController(language) { next ->
            language = next
            persist(next)
            window.document.documentElement?.setAttribute("lang", next.tag)
        }
    }
    LaunchedEffect(language) {
        window.document.documentElement?.setAttribute("lang", language.tag)
    }
    CompositionLocalProvider(
        LocalSiteLanguage provides language,
        LocalSiteStrings provides strings,
        LocalSiteLanguageController provides controller,
    ) {
        content()
    }
}

private fun readInitialLanguage(): SiteLanguage {
    val stored = try {
        localStorage[STORAGE_KEY]
    } catch (_: Throwable) {
        null
    }
    if (stored != null) return SiteLanguage.fromTag(stored)
    val browser = try {
        window.navigator.language.substringBefore('-').lowercase()
    } catch (_: Throwable) {
        null
    }
    return if (browser == "en") SiteLanguage.EN else SiteLanguage.NL
}

private fun persist(language: SiteLanguage) {
    try {
        localStorage[STORAGE_KEY] = language.tag
    } catch (_: Throwable) {
        // localStorage may be unavailable in some contexts; silently ignore
    }
}
